import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Divider,
  IconButton,
} from "@mui/material";
import {
  QrCode2,
  PointOfSale,
  Kitchen,
  TrendingUp,
  RestaurantMenu,
  People,
  ArrowForward,
  CheckCircle,
  Instagram,
  Facebook,
  LinkedIn,
  Bolt,
  CreditCardOff,
  TimerOutlined,
  WorkspacePremiumOutlined,
} from "@mui/icons-material";
import { Twitter } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { PublicHeader } from "components";

const BRAND = "#F9735B";
const BRAND_DARK = "#DC5F49";
const DARK_BG = "#050B16";
const DARK_PANEL = "rgba(255,255,255,0.075)";
const DARK_BORDER = "rgba(255,255,255,0.14)";
const DARK_TEXT = "rgba(255,255,255,0.72)";
const HERO_VIDEO = "/assets/vedios/digital-restaurant-system-demo.mp4";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const STEPS = [
  {
    n: "01",
    title: "Create Your Account",
    desc: "Start in minutes with a guided setup made for busy restaurant teams.",
  },
  {
    n: "02",
    title: "Build Your Live Menu",
    desc: "Add items, pricing, photos, and categories. Publish updates instantly.",
  },
  {
    n: "03",
    title: "Take Orders in Real Time",
    desc: "QR scans become live orders that flow directly to POS and kitchen screens.",
  },
];

const FEATURES = [
  {
    icon: <QrCode2 />,
    title: "QR Menu",
    tag: "Fast",
    tagColor: "#0EA5E9",
    accent: "#0EA5E9",
    desc: "Guests scan and order immediately with a clear menu experience on any phone.",
    highlight: "No app installs required.",
  },
  {
    icon: <PointOfSale />,
    title: "POS Control",
    tag: "Core",
    tagColor: "#10B981",
    accent: "#10B981",
    desc: "Manage tables, billing, and service flow from one practical operations screen.",
    highlight: "Built for lunch and dinner rush.",
  },
  {
    icon: <Kitchen />,
    title: "Kitchen Display",
    tag: "Live",
    tagColor: "#F97316",
    accent: "#F97316",
    desc: "Orders appear instantly with clear item details to reduce ticket confusion.",
    highlight: "Less error, faster handoff.",
  },
  {
    icon: <TrendingUp />,
    title: "Analytics",
    tag: "Insight",
    tagColor: "#334155",
    accent: "#334155",
    desc: "Track revenue, peak hours, and top sellers to improve margin and planning.",
    highlight: "Data for better decisions.",
  },
  {
    icon: <RestaurantMenu />,
    title: "Menu Updates",
    tag: "Easy",
    tagColor: BRAND,
    accent: BRAND,
    desc: "Change prices, add specials, or mark items sold out in seconds.",
    highlight: "Updates go live instantly.",
  },
  {
    icon: <People />,
    title: "Multi-Branch",
    tag: "Scale",
    tagColor: "#7C3AED",
    accent: "#7C3AED",
    desc: "Operate multiple locations from a unified dashboard without losing control.",
    highlight: "One workspace for every outlet.",
  },
];

const CORE_FEATURES = [
  {
    icon: <QrCode2 />,
    title: "QR Menu",
    subtitle: "Scan. Browse. Order.",
    desc: "Customers open your full menu instantly by scanning a table QR code.",
    accent: "#0EA5E9",
    tone: "rgba(14,165,233,0.10)",
  },
  {
    icon: <PointOfSale />,
    title: "POS",
    subtitle: "One Screen Control",
    desc: "Handle tables, billing, and orders quickly with an intuitive POS workflow.",
    accent: "#10B981",
    tone: "rgba(16,185,129,0.10)",
  },
  {
    icon: <Kitchen />,
    title: "Kitchen Display",
    subtitle: "Live Order Flow",
    desc: "Send every order to kitchen in real time with clear item-level instructions.",
    accent: "#F97316",
    tone: "rgba(249,115,22,0.10)",
  },
];

const FOOTER_LINKS = {
  Platform: ["Browse Restaurants", "Register Restaurant", "How It Works"],
  Company: ["About Us", "Blog", "Careers", "Contact"],
};

const SOCIAL = [
  { icon: <Instagram fontSize="small" />, label: "Instagram" },
  { icon: <Facebook fontSize="small" />, label: "Facebook" },
  { icon: <Twitter fontSize="small" />, label: "Twitter" },
  { icon: <LinkedIn fontSize="small" />, label: "LinkedIn" },
];

const TRUST_BADGES = [
  {
    icon: <CreditCardOff />,
    label: "No credit card",
    accent: "#38BDF8",
    tone: "rgba(56,189,248,0.12)",
  },
  {
    icon: <TimerOutlined />,
    label: "Setup in 10 minutes",
    accent: "#86EFAC",
    tone: "rgba(134,239,172,0.12)",
  },
  {
    icon: <WorkspacePremiumOutlined />,
    label: "Free starter plan",
    accent: "#F9735B",
    tone: "rgba(249,115,91,0.14)",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const { scrollYProgress: pageScroll } = useScroll();
  const sectionTextureY = useSpring(useTransform(pageScroll, [0, 1], [0, -120]), { stiffness: 55, damping: 22 });

  return (
    <Box sx={{ background: DARK_BG, minHeight: "100vh" }}>
      <PublicHeader variant="darkHero" />

      <Box
        component="section"
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: { xs: "100vh", md: "100vh" },
          "@supports (height: 100svh)": {
            minHeight: { xs: "100svh", md: "100svh" },
          },
          display: "flex",
          alignItems: "center",
          pt: { xs: "calc(70px + 2rem)", md: "calc(80px + 3.2rem)" },
          pb: { xs: 5.5, md: 6.5 },
          bgcolor: DARK_BG,
          borderBottom: `1px solid ${DARK_BORDER}`,
        }}
      >
        <Box
          component="video"
          src={HERO_VIDEO}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          fetchPriority="high"
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            filter: "saturate(1.05) contrast(1.06)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, rgba(5,11,22,0.90) 0%, rgba(5,11,22,0.72) 48%, rgba(5,11,22,0.38) 100%), linear-gradient(180deg, rgba(5,11,22,0.20) 0%, rgba(5,11,22,0.82) 100%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "linear-gradient(90deg, #000 0%, rgba(0,0,0,0.72) 48%, transparent 100%)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, width: "100%" }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <motion.div initial="hidden" animate="visible" variants={stagger}>
                <motion.div variants={fadeUp}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      border: "1px solid rgba(255,255,255,0.18)",
                      borderRadius: "10px",
                      px: 1.8,
                      py: 0.8,
                      bgcolor: "rgba(255,255,255,0.08)",
                      backdropFilter: "blur(10px)",
                      mb: 2.8,
                    }}
                  >
                    <Bolt sx={{ fontSize: 15, color: "#FFD166" }} />
                    <Typography
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: "rgba(255,255,255,0.84)",
                        fontFamily: '"exo", "Inter", sans-serif',
                        letterSpacing: 0,
                      }}
                    >
                      Built for modern restaurants in Sri Lanka
                    </Typography>
                  </Box>
                </motion.div>

                <motion.div variants={fadeUp}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontFamily: '"exo", "Inter", sans-serif',
                      fontWeight: 700,
                      fontSize: { xs: "2.55rem", sm: "3.6rem", md: "4.85rem" },
                      lineHeight: 1.03,
                      letterSpacing: 0,
                      color: "#fff",
                      mb: 2.2,
                      maxWidth: 760,
                    }}
                  >
                    Restaurant service that moves beautifully.
                  </Typography>
                </motion.div>

                <motion.div variants={fadeUp}>
                  <Typography
                    sx={{
                      fontSize: { xs: "1rem", md: "1.14rem" },
                      color: "rgba(255,255,255,0.74)",
                      lineHeight: 1.78,
                      fontFamily: '"Inter", sans-serif',
                      maxWidth: 620,
                      mb: 4,
                    }}
                  >
                    Menux brings QR menus, POS, kitchen display, and live order tracking into one polished flow for faster tables and calmer teams.
                  </Typography>
                </motion.div>

                <motion.div variants={fadeUp}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate("/company-registration")}
                      endIcon={<ArrowForward />}
                      sx={{
                        bgcolor: BRAND,
                        color: "#fff",
                        fontWeight: 700,
                        fontFamily: '"exo", "Inter", sans-serif',
                        fontSize: "0.96rem",
                        px: 4,
                        py: 1.6,
                        borderRadius: "12px",
                        textTransform: "none",
                        boxShadow: "0 18px 42px rgba(249,115,91,0.34)",
                        "&:hover": { bgcolor: BRAND_DARK, boxShadow: "0 20px 48px rgba(249,115,91,0.42)" },
                      }}
                    >
                      Register Your Restaurant
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate("/restaurants")}
                      sx={{
                        color: "#fff",
                        borderColor: "rgba(255,255,255,0.28)",
                        bgcolor: "rgba(255,255,255,0.08)",
                        fontWeight: 700,
                        fontFamily: '"exo", "Inter", sans-serif',
                        fontSize: "0.96rem",
                        px: 4,
                        py: 1.6,
                        borderRadius: "12px",
                        textTransform: "none",
                        backdropFilter: "blur(10px)",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.14)", borderColor: "rgba(255,255,255,0.48)" },
                      }}
                    >
                      Browse Restaurants
                    </Button>
                  </Stack>
                </motion.div>

                <motion.div variants={fadeUp}>
                  <Stack
                    direction="row"
                    spacing={{ xs: 1, sm: 1.2 }}
                    sx={{ mt: 3.4 }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {TRUST_BADGES.map((badge) => (
                      <Stack
                        key={badge.label}
                        direction="row"
                        alignItems="center"
                        spacing={0.9}
                        sx={{
                          px: 1.25,
                          py: 0.85,
                          borderRadius: "10px",
                          bgcolor: "rgba(255,255,255,0.075)",
                          border: "1px solid rgba(255,255,255,0.14)",
                          backdropFilter: "blur(12px)",
                          boxShadow: "0 10px 26px rgba(0,0,0,0.16)",
                        }}
                      >
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: badge.tone,
                            color: badge.accent,
                            flexShrink: 0,
                            "& svg": { fontSize: 17 },
                          }}
                        >
                          {badge.icon}
                        </Box>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "rgba(255,255,255,0.78)", fontFamily: '"exo", "Inter", sans-serif' }}>
                          {badge.label}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </motion.div>
              </motion.div>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.28, ease: "easeOut" }}>
                <Box
                  sx={{
                    border: "1px solid rgba(255,255,255,0.18)",
                    borderRadius: "14px",
                    bgcolor: "rgba(255,255,255,0.08)",
                    backdropFilter: "blur(16px)",
                    boxShadow: "0 24px 70px rgba(0,0,0,0.34)",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: 1.2, borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      {["#FF6B6B", "#FFD166", "#06D6A0"].map((color) => (
                        <Box key={color} sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: color }} />
                      ))}
                      <Typography sx={{ color: "rgba(255,255,255,0.64)", fontSize: "0.72rem", fontWeight: 700, fontFamily: '"exo", "Inter", sans-serif', pl: 0.7 }}>
                        Live restaurant workflow
                      </Typography>
                    </Stack>
                  </Box>
                  <Box sx={{ position: "relative", aspectRatio: "16 / 11", bgcolor: "#020617" }}>
                    <Box
                      component="video"
                      src={HERO_VIDEO}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="auto"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(180deg, transparent 45%, rgba(2,6,23,0.76) 100%)",
                      }}
                    />
                    <Stack
                      spacing={0.8}
                      sx={{
                        position: "absolute",
                        top: 14,
                        right: 14,
                        width: { xs: 154, sm: 170 },
                      }}
                    >
                      {[
                        { text: "Table QR scanned", color: "#38BDF8", delay: 0 },
                        { text: "POS ticket opened", color: "#86EFAC", delay: 0.45 },
                        { text: "Kitchen synced", color: "#F9735B", delay: 0.9 },
                      ].map((item) => (
                        <Box
                          key={item.text}
                          component={motion.div}
                          initial={{ opacity: 0, x: 18 }}
                          animate={{ opacity: [0.45, 1, 0.78], x: 0 }}
                          transition={{
                            duration: 1.8,
                            delay: item.delay,
                            repeat: Infinity,
                            repeatDelay: 2.4,
                            ease: "easeOut",
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            px: 1,
                            py: 0.75,
                            borderRadius: "10px",
                            border: "1px solid rgba(255,255,255,0.14)",
                            bgcolor: "rgba(2,6,23,0.48)",
                            backdropFilter: "blur(12px)",
                          }}
                        >
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: item.color, boxShadow: `0 0 16px ${item.color}` }} />
                          <Typography sx={{ color: "rgba(255,255,255,0.82)", fontSize: "0.67rem", fontWeight: 700, fontFamily: '"exo", "Inter", sans-serif' }}>
                            {item.text}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        position: "absolute",
                        left: 16,
                        right: 16,
                        bottom: 16,
                      }}
                    >
                      {[
                        { label: "QR orders", value: "Live", color: "#0EA5E9" },
                        { label: "Kitchen", value: "Synced", color: "#F97316" },
                      ].map((item) => (
                        <Box
                          key={item.label}
                          sx={{
                            flex: 1,
                            p: 1.4,
                            borderRadius: "12px",
                            bgcolor: "rgba(255,255,255,0.12)",
                            border: "1px solid rgba(255,255,255,0.16)",
                            backdropFilter: "blur(12px)",
                          }}
                        >
                          <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: "0.68rem", fontFamily: '"Inter", sans-serif' }}>
                            {item.label}
                          </Typography>
                          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", fontFamily: '"exo", "Inter", sans-serif' }}>
                            {item.value}
                          </Typography>
                          <Box sx={{ mt: 0.8, height: 3, borderRadius: "999px", bgcolor: item.color }} />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.48, ease: "easeOut" }}>
            <Grid
              container
              spacing={0}
              sx={{
                mt: { xs: 4, md: 5 },
                border: `1px solid ${DARK_BORDER}`,
                borderRadius: "14px",
                bgcolor: "rgba(255,255,255,0.08)",
                backdropFilter: "blur(16px)",
                boxShadow: "0 22px 58px rgba(0,0,0,0.30)",
                overflow: "hidden",
              }}
            >
              {CORE_FEATURES.map((feature) => (
                <Grid key={feature.title} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    sx={{
                      p: { xs: 1.8, md: 2.1 },
                      borderRight: {
                        md: feature.title !== "Kitchen Display" ? `1px solid ${DARK_BORDER}` : "none",
                      },
                      borderBottom: {
                        xs: feature.title !== "Kitchen Display" ? `1px solid ${DARK_BORDER}` : "none",
                        sm: feature.title === "QR Menu" ? `1px solid ${DARK_BORDER}` : "none",
                        md: "none",
                      },
                      height: "100%",
                      transition: "background 0.2s ease",
                      "&:hover": {
                        background: `${feature.tone}`,
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          bgcolor: feature.tone,
                          border: `1px solid ${feature.accent}66`,
                          color: feature.accent,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          "& svg": { fontSize: "1.25rem" },
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: { xs: "0.94rem", md: "0.98rem" }, color: "#fff", fontFamily: '"exo", "Inter", sans-serif', lineHeight: 1.2 }}>
                          {feature.title}
                        </Typography>
                        <Typography sx={{ fontSize: "0.67rem", color: feature.accent, fontWeight: 700, letterSpacing: 0, textTransform: "uppercase", fontFamily: '"exo", "Inter", sans-serif', mt: 0.25 }}>
                          {feature.subtitle}
                        </Typography>
                      </Box>
                    </Stack>
                    <Typography sx={{ mt: 1.15, color: DARK_TEXT, fontSize: "0.79rem", lineHeight: 1.55, fontFamily: '"Inter", sans-serif' }}>
                      {feature.desc}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          borderBottom: `1px solid ${DARK_BORDER}`,
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#050B16",
          backgroundImage: [
            "radial-gradient(circle at 12% 12%, rgba(14,165,233,0.18), transparent 30%)",
            "radial-gradient(circle at 90% 8%, rgba(249,115,91,0.18), transparent 32%)",
            "linear-gradient(180deg, rgba(5,11,22,0.82) 0%, rgba(5,11,22,0.92) 100%)",
            "url('/assets/images/hero_section_2_back.webp')",
          ].join(", "),
          backgroundSize: "auto, auto, auto, cover",
          backgroundPosition: "0 0, 0 0, 0 0, center",
          backgroundRepeat: "no-repeat, no-repeat, no-repeat, no-repeat",
        }}
      >
        <Container maxWidth="lg">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 }, position: "relative", zIndex: 1 }}>
                <Typography
                  sx={{
                    display: "inline-block",
                    px: 1.7,
                    py: 0.5,
                    borderRadius: "10px",
                    bgcolor: DARK_PANEL,
                    border: `1px solid ${DARK_BORDER}`,
                    mb: 1.8,
                    fontSize: "0.69rem",
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.72)",
                    letterSpacing: 0,
                    textTransform: "uppercase",
                    fontFamily: '"exo", "Inter", sans-serif',
                  }}
                >
                  How It Works
                </Typography>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    letterSpacing: 0,
                    fontSize: { xs: "2rem", md: "2.95rem" },
                    color: "#fff",
                    fontFamily: '"exo", "Inter", sans-serif',
                    mb: 1.2,
                    lineHeight: 1.08,
                  }}
                >
                  Launch in 3 clear steps
                </Typography>
                <Typography
                  sx={{
                    textAlign: "center",
                    color: DARK_TEXT,
                    maxWidth: 560,
                    mx: "auto",
                    fontSize: { xs: "0.92rem", md: "1rem" },
                    lineHeight: 1.72,
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  From signup to your first live order, every step is designed to be quick, clear, and team-friendly.
                </Typography>
              </Box>
            </motion.div>

            <Grid container spacing={{ xs: 2.2, md: 2.6 }} sx={{ position: "relative", zIndex: 1 }}>
              {STEPS.map((step, i) => (
                <Grid key={step.n} size={{ xs: 12, md: 4 }}>
                  <motion.div variants={fadeUp}>
                    <Box
                      sx={{
                        p: { xs: 2.6, md: 2.9 },
                        borderRadius: "14px",
                        border: `1px solid ${DARK_BORDER}`,
                        bgcolor: DARK_PANEL,
                        minHeight: 242,
                        boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
                        position: "relative",
                        backdropFilter: "blur(4px)",
                        overflow: "hidden",
                        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
                        "&:hover": {
                          transform: "translateY(-6px)",
                          borderColor: i === 0 ? "#0EA5E9" : i === 1 ? BRAND : "#F97316",
                          boxShadow: "0 24px 60px rgba(0,0,0,0.32)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: 0,
                          height: 4,
                          background:
                            i === 0
                              ? "linear-gradient(90deg, #0EA5E9 0%, #38BDF8 100%)"
                              : i === 1
                                ? "linear-gradient(90deg, #F9735B 0%, #FB7185 100%)"
                                : "linear-gradient(90deg, #F97316 0%, #FB923C 100%)",
                        }}
                      />
                      {i < STEPS.length - 1 && (
                        <Box
                          sx={{
                            display: { xs: "none", md: "block" },
                            position: "absolute",
                            top: 48,
                            right: "-24px",
                            width: 24,
                            height: 2,
                            bgcolor: "rgba(255,255,255,0.28)",
                            borderRadius: "99px",
                          }}
                        />
                      )}
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: "14px",
                          bgcolor: i === 0 ? "#0EA5E9" : i === 1 ? BRAND : "#F97316",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2.1,
                          boxShadow: "0 12px 24px rgba(15,23,42,0.15)",
                        }}
                      >
                        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.9rem", fontFamily: '"exo", "Inter", sans-serif' }}>
                          {step.n}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "1.17rem", color: "#fff", fontFamily: '"exo", "Inter", sans-serif', mb: 0.95, lineHeight: 1.24 }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ color: DARK_TEXT, fontSize: "0.88rem", lineHeight: 1.72, fontFamily: '"Inter", sans-serif', mb: 1.7 }}>
                        {step.desc}
                      </Typography>
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.7, px: 1.1, py: 0.5, borderRadius: "10px", border: `1px solid ${DARK_BORDER}`, bgcolor: "rgba(255,255,255,0.08)" }}>
                        <CheckCircle sx={{ fontSize: 15, color: i === 0 ? "#0EA5E9" : i === 1 ? BRAND : "#F97316" }} />
                        <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: "rgba(255,255,255,0.66)", fontFamily: '"Inter", sans-serif' }}>
                          Fast setup
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: DARK_BG,
          borderBottom: `1px solid ${DARK_BORDER}`,
          position: "relative",
          overflow: "hidden",
          background:
            "radial-gradient(circle at 82% 12%, rgba(249,115,91,0.13), transparent 30%), radial-gradient(circle at 12% 82%, rgba(16,185,129,0.10), transparent 30%), #050B16",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
            opacity: 0.45,
            pointerEvents: "none",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: "-10% 0",
            backgroundImage: "radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)",
            backgroundSize: "34px 34px",
            pointerEvents: "none",
            opacity: 0.35,
          }}
        />
        <Container maxWidth="lg">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 }, position: "relative", zIndex: 1 }}>
                <Typography
                  sx={{
                    display: "inline-block",
                    px: 1.8,
                    py: 0.55,
                    borderRadius: "10px",
                    bgcolor: "rgba(249,115,91,0.10)",
                    border: "1px solid rgba(249,115,91,0.26)",
                    mb: 2,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: BRAND,
                    letterSpacing: 0,
                    textTransform: "uppercase",
                    fontFamily: '"exo", "Inter", sans-serif',
                  }}
                >
                  Platform Features
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0,
                    fontSize: { xs: "1.9rem", md: "2.8rem" },
                    color: "#fff",
                    fontFamily: '"exo", "Inter", sans-serif',
                    lineHeight: 1.1,
                    mb: 1.3,
                  }}
                >
                  Everything needed to run service smoothly
                </Typography>
                <Typography
                  sx={{
                    color: DARK_TEXT,
                    maxWidth: 560,
                    mx: "auto",
                    fontSize: "0.98rem",
                    lineHeight: 1.75,
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  One system from guest scan to kitchen output, billing, and reporting.
                </Typography>
              </Box>
            </motion.div>

            <Grid container spacing={2.3} sx={{ position: "relative", zIndex: 1 }}>
              {FEATURES.map((f) => (
                <Grid key={f.title} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    component={motion.div}
                    variants={fadeUp}
                    whileHover={{ y: -6 }}
                    transition={{ type: "spring", stiffness: 300, damping: 22 }}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.095)",
                      border: `1px solid ${DARK_BORDER}`,
                      borderRadius: "14px",
                      p: 3,
                      minHeight: 260,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      boxShadow: "0 18px 46px rgba(0,0,0,0.20)",
                      transition: "box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease",
                      "&:hover": {
                        borderColor: f.accent,
                        bgcolor: "rgba(255,255,255,0.12)",
                        boxShadow: "0 20px 54px rgba(0,0,0,0.28)",
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: "14px",
                          bgcolor: `${f.accent}1F`,
                          border: `1px solid ${f.accent}55`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: f.accent,
                          "& svg": { fontSize: "1.5rem" },
                        }}
                      >
                        {f.icon}
                      </Box>
                      <Box
                        sx={{
                          px: 1.3,
                          py: 0.45,
                          borderRadius: "999px",
                          bgcolor: `${f.tagColor}18`,
                          border: `1px solid ${f.tagColor}44`,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color: f.tagColor, fontFamily: '"exo", "Inter", sans-serif', letterSpacing: 0, textTransform: "uppercase" }}>
                          {f.tag}
                        </Typography>
                      </Box>
                    </Stack>

                    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#fff", fontFamily: '"exo", "Inter", sans-serif', mb: 1 }}>
                      {f.title}
                    </Typography>
                    <Typography sx={{ color: DARK_TEXT, fontSize: "0.88rem", lineHeight: 1.72, fontFamily: '"Inter", sans-serif', mb: 2.2, flex: 1 }}>
                      {f.desc}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, px: 1.5, py: 0.9, borderRadius: "10px", bgcolor: "rgba(255,255,255,0.08)", border: `1px solid ${DARK_BORDER}` }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: f.accent, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.77rem", fontWeight: 600, color: "rgba(255,255,255,0.86)", fontFamily: '"Inter", sans-serif' }}>
                        {f.highlight}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 11 },
          bgcolor: "#081122",
          position: "relative",
          overflow: "hidden",
          borderBottom: `1px solid ${DARK_BORDER}`,
          background:
            "linear-gradient(135deg, rgba(249,115,91,0.12) 0%, transparent 32%), linear-gradient(180deg, #081122 0%, #050B16 100%)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
            backgroundSize: "46px 46px",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <Typography
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0,
                  fontSize: { xs: "2rem", md: "2.9rem" },
                  color: "#fff",
                  lineHeight: 1.1,
                  fontFamily: '"exo", "Inter", sans-serif',
                  mb: 1.8,
                }}
              >
                Ready to modernize your restaurant flow?
              </Typography>
            </motion.div>

            <motion.div variants={fadeUp}>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.62)",
                  fontSize: "1rem",
                  lineHeight: 1.75,
                  fontFamily: '"Inter", sans-serif',
                  maxWidth: 500,
                  mx: "auto",
                  mb: 4.5,
                }}
              >
                Join restaurants already using Menux to serve faster and scale with confidence.
              </Typography>
            </motion.div>

            <motion.div variants={fadeUp}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.7} justifyContent="center">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/company-registration")}
                  endIcon={<ArrowForward />}
                  sx={{
                    bgcolor: BRAND,
                    color: "#fff",
                    fontWeight: 700,
                    fontFamily: '"exo", "Inter", sans-serif',
                    fontSize: "0.95rem",
                    px: 4.5,
                    py: 1.55,
                    borderRadius: "12px",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": { bgcolor: BRAND_DARK, boxShadow: "none" },
                  }}
                >
                  Register Your Restaurant
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/login")}
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.28)",
                    fontWeight: 700,
                    fontFamily: '"exo", "Inter", sans-serif',
                    fontSize: "0.95rem",
                    px: 4,
                    py: 1.55,
                    borderRadius: "12px",
                    textTransform: "none",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.46)" },
                  }}
                >
                  Sign In
                </Button>
              </Stack>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          bgcolor: DARK_BG,
          borderTop: `1px solid ${DARK_BORDER}`,
          py: { xs: 7, md: 9 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: "radial-gradient(circle at 15% 0%, rgba(14,165,233,0.10), transparent 32%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box
          component={motion.div}
          style={{ y: sectionTextureY }}
          sx={{
            position: "absolute",
            inset: "-12% 0",
            backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.032) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            pointerEvents: "none",
            opacity: 0.42,
          }}
        />
        <Container maxWidth="lg">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} variants={stagger}>
            <Grid container spacing={{ xs: 4.5, md: 7 }} sx={{ position: "relative", zIndex: 1 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <motion.div variants={fadeUp}>
                  <Box component="img" src="/assets/logo.png" alt="Menux" sx={{ height: 28, width: "auto", mb: 2.2 }} />
                  <Typography sx={{ fontSize: "0.84rem", color: DARK_TEXT, lineHeight: 1.75, fontFamily: '"Inter", sans-serif', maxWidth: 300, mb: 2.5 }}>
                    Sri Lanka's restaurant discovery and digital operations platform.
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {SOCIAL.map((s) => (
                      <IconButton
                        key={s.label}
                        component={motion.button}
                        whileHover={{ y: -3, scale: 1.08 }}
                        whileTap={{ scale: 0.94 }}
                        size="small"
                        aria-label={s.label}
                        sx={{
                          color: "rgba(255,255,255,0.68)",
                          border: `1px solid ${DARK_BORDER}`,
                          bgcolor: "rgba(255,255,255,0.06)",
                          "&:hover": { color: BRAND, borderColor: BRAND, bgcolor: "rgba(249,115,91,0.10)" },
                          transition: "color 0.18s, border-color 0.18s, background 0.18s",
                        }}
                      >
                        {s.icon}
                      </IconButton>
                    ))}
                  </Stack>
                </motion.div>
              </Grid>

              {Object.entries(FOOTER_LINKS).map(([heading, items]) => (
                <Grid key={heading} size={{ xs: 6, md: 2 }}>
                  <motion.div variants={fadeUp}>
                    <Typography sx={{ fontSize: "0.67rem", fontWeight: 700, letterSpacing: 0, textTransform: "uppercase", color: "rgba(255,255,255,0.46)", mb: 2.1, fontFamily: '"exo", "Inter", sans-serif' }}>
                      {heading}
                    </Typography>
                    <Stack spacing={1.3}>
                      {items.map((link) => (
                        <Typography
                          key={link}
                          onClick={() => {
                            if (link === "Browse Restaurants") navigate("/restaurants");
                            if (link === "Register Restaurant") navigate("/company-registration");
                          }}
                          sx={{
                            fontSize: "0.84rem",
                            color: DARK_TEXT,
                            cursor: "pointer",
                            fontFamily: '"Inter", sans-serif',
                            "&:hover": { color: BRAND },
                            transition: "color 0.16s",
                          }}
                        >
                          {link}
                        </Typography>
                      ))}
                    </Stack>
                  </motion.div>
                </Grid>
              ))}

              <Grid size={{ xs: 12, md: 4 }}>
                <motion.div variants={fadeUp}>
                  <Typography sx={{ fontSize: "0.67rem", fontWeight: 700, letterSpacing: 0, textTransform: "uppercase", color: "rgba(255,255,255,0.46)", mb: 2.1, fontFamily: '"exo", "Inter", sans-serif' }}>
                    Stay Updated
                  </Typography>
                  <Typography sx={{ fontSize: "0.84rem", color: DARK_TEXT, mb: 2.1, lineHeight: 1.72, fontFamily: '"Inter", sans-serif' }}>
                    Product updates and practical tips for better service.
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      border: `1px solid ${DARK_BORDER}`,
                      borderRadius: "10px",
                      overflow: "hidden",
                      bgcolor: "rgba(255,255,255,0.07)",
                      "&:focus-within": { borderColor: BRAND },
                      transition: "border-color 0.2s",
                    }}
                  >
                    <Box
                      component="input"
                      placeholder="you@example.com"
                      sx={{
                        flex: 1,
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        color: "#fff",
                        fontSize: "0.84rem",
                        px: 1.8,
                        py: 1.15,
                        fontFamily: '"Inter", sans-serif',
                        "::placeholder": { color: "rgba(255,255,255,0.42)" },
                      }}
                    />
                    <Box
                      component={motion.button}
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.96 }}
                      sx={{
                        px: 2.3,
                        border: "none",
                        cursor: "pointer",
                        bgcolor: BRAND,
                        color: "#fff",
                        fontSize: "0.77rem",
                        fontWeight: 700,
                        fontFamily: '"exo", "Inter", sans-serif',
                        m: 0.45,
                        borderRadius: "8px",
                        minHeight: 34,
                        "&:hover": { bgcolor: BRAND_DARK },
                        transition: "background 0.18s",
                      }}
                    >
                      Subscribe
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>

            <motion.div variants={fadeUp}>
              <Divider sx={{ my: { xs: 4.5, md: 5.5 }, borderColor: DARK_BORDER, position: "relative", zIndex: 1 }} />

              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} sx={{ position: "relative", zIndex: 1 }}>
                <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.44)", fontFamily: '"Inter", sans-serif' }}>
                  Copyright {new Date().getFullYear()} Menux. All rights reserved.
                </Typography>
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  {["Privacy Policy", "Terms of Service", "Cookies"].map((l) => (
                    <Typography key={l} sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.44)", cursor: "pointer", fontFamily: '"Inter", sans-serif', "&:hover": { color: BRAND }, transition: "color 0.16s" }}>
                      {l}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}
